/*
 * DAC product tabs (sections/dac-product-tabs.liquid): an accessible tab list. Arrow keys / Home / End move between tabs,
 * and activate(type) lets other parts of the page (the "Read more" link under the product title) open a tab by its type.
 */
/*
 * Tidies the product description pasted into Shopify's editor. Descriptions in this store are all <p> + <strong> with
 * stray <meta> tags, inline font overrides (a 1.4em bold "Colours in range:" etc.), <br> padding and empty paragraphs.
 * This removes the junk, keeps deliberate text colours, turns lone bold lines ending in ":" into headings and lays the
 * colour list out as a code / name grid.
 */
const isBlank = (element) => !element.textContent.replace(/[\s ]/g, '') && !element.querySelector('img, video, iframe, svg, table, hr');

/*
 * One colour row = exactly two cells: the bold code and everything else. The descriptions are typed by hand, so a row can be:
 *   <strong>A1</strong><span orange><strong> POLARISED</strong></span> Horizontal Black ...   (tag as its own element)
 *   <strong>A203 <span orange>POLARISED</span></strong> Royal Blue ...                         (code and tag in one bold)
 *   LAST PAIR!!! <strong>8KM</strong> Soft Pink ...                                           (a note before the code)
 *   <strong>E6H </strong>Warm White ...                                                       (stray space in the bold)
 * Left as they are, the extra elements become extra grid cells and push the name under the code. Here the code is the first
 * word of the first bold part (upper case letters / digits / "/" "-"), any note and anything else left in that bold part
 * goes in front of the name, and a name that was typed twice in a row is shown once.
 */
const COLOUR_CODE = /^[A-Z0-9][A-Z0-9/.\-]{0,13}$/;

const buildColourRow = (paragraph) => {
  const nodes = Array.from(paragraph.childNodes);
  const codeIndex = nodes.findIndex((node) => node.nodeType === 1 && /^(STRONG|B)$/.test(node.nodeName));

  if (codeIndex === -1) {
    return;
  }

  // Only blank text or a short upper case note ("LAST PAIR!!!") may come before the code
  const before = nodes.slice(0, codeIndex);
  const note = before.map((node) => node.textContent).join('').replace(/\s+/g, ' ').trim();

  if (before.some((node) => node.nodeType !== 3 && node.textContent.trim()) || (note && !/^[A-Z0-9 !?.,'&-]{2,30}$/.test(note))) {
    return;
  }

  const codeElement = nodes[codeIndex];
  const token = codeElement.textContent.trim().split(/\s+/)[0];

  if (!COLOUR_CODE.test(token)) {
    return;
  }

  // Take the code off the front of the bold part; whatever is left (a POLARISED tag) goes with the name
  const firstText = Array.from(codeElement.childNodes).find((node) => node.nodeType === 3 && node.textContent.trim());

  if (firstText) {
    firstText.textContent = firstText.textContent.replace(/^\s*\S+/, '');
  }

  const code = document.createElement('strong');
  code.className = 'dac-description__code';
  code.textContent = token;

  const name = document.createElement('span');
  name.className = 'dac-description__colour-name';

  if (note) {
    const noteElement = document.createElement('span');
    noteElement.className = 'dac-description__note';
    noteElement.textContent = note;
    name.append(noteElement, ' ');
  }

  if (codeElement.textContent.trim()) {
    name.append(codeElement, ' ');
  }

  nodes.slice(codeIndex + 1).forEach((node) => name.append(node));

  // Trim the spaces around the name text and show a name that was typed twice in a row only once
  const lastText = Array.from(name.childNodes).reverse().find((node) => node.nodeType === 3 && node.textContent.trim());

  if (lastText) {
    const words = lastText.textContent.replace(/\s+/g, ' ').trim().split(' ');
    const half = words.length / 2;
    // keep one space when the name follows another element (the POLARISED tag)
    const lead = lastText.previousSibling && /^\s/.test(lastText.textContent) ? ' ' : '';

    lastText.textContent = lead + (Number.isInteger(half) && half > 1 && words.slice(0, half).join(' ') === words.slice(half).join(' ')
      ? words.slice(0, half).join(' ')
      : words.join(' '));
  }

  paragraph.replaceChildren(code, name);
  paragraph.classList.add('dac-description__colour');
};

const tidyDescription = (container) => {
  const body = container.querySelector('[data-description-body]');

  if (!body || body.dataset.tidied) {
    container.removeAttribute('data-tidy-pending');
    return;
  }

  body.dataset.tidied = 'true';

  body.querySelectorAll('meta, style, script').forEach((element) => element.remove());

  // Inline styles: keep a deliberate text colour, drop fonts, sizes, weights and so on
  body.querySelectorAll('[style]').forEach((element) => {
    const color = element.style.color;
    element.removeAttribute('style');

    if (color) {
      element.style.color = color;
    }
  });

  body.querySelectorAll('font').forEach((element) => element.replaceWith(...element.childNodes));
  body.querySelectorAll('span:not([style])').forEach((element) => element.replaceWith(...element.childNodes));

  // Empty bold / italic / link shells left behind by the editor
  body.querySelectorAll('strong, b, em, i, a:not([href])').forEach((element) => {
    if (!element.textContent.trim() && !element.querySelector('img')) {
      element.remove();
    }
  });

  // <div> blocks that only hold text and inline tags are paragraphs
  body.querySelectorAll('div').forEach((element) => {
    if (!element.querySelector('p, div, ul, ol, table, h1, h2, h3, h4, h5, h6, blockquote, img, figure')) {
      const paragraph = document.createElement('p');
      paragraph.append(...element.childNodes);
      element.replaceWith(paragraph);
    }
  });

  // <br> padding at the start / end of paragraphs, and runs of <br>
  body.querySelectorAll('p, div').forEach((element) => {
    while (element.firstChild && ((element.firstChild.nodeName === 'BR') || (element.firstChild.nodeType === 3 && !element.firstChild.textContent.trim()))) {
      element.firstChild.remove();
    }

    while (element.lastChild && ((element.lastChild.nodeName === 'BR') || (element.lastChild.nodeType === 3 && !element.lastChild.textContent.trim()))) {
      element.lastChild.remove();
    }
  });

  body.querySelectorAll('br + br').forEach((element) => element.remove());

  // A line that only says "Free Shipping" or "Free Shipping in Australia" (typed into many older descriptions) is not shown. The descriptions themselves are left as typed.
  body.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach((element) => {
    if (/^free\s+shipping(\s+(with)?in\s+(australia|aus))?[\s.!]*$/i.test(element.textContent.replace(/ /g, ' ').trim())) {
      element.remove();
    }
  });

  // Empty paragraphs / blocks
  body.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach((element) => {
    if (isBlank(element)) {
      element.remove();
    }
  });

  // A paragraph that is only a short bold line ending in ":" is a heading
  body.querySelectorAll('p').forEach((paragraph) => {
    const children = Array.from(paragraph.childNodes).filter((node) => node.nodeType === 1 || node.textContent.trim());
    const text = paragraph.textContent.trim();

    if (children.length === 1 && /^(STRONG|B)$/.test(children[0].nodeName) && text.length <= 60 && text.endsWith(':')) {
      const heading = document.createElement('h4');
      heading.className = 'dac-description__heading';
      // Shown without the trailing ":" (the product description itself is left as typed)
      heading.textContent = text.replace(/\s*:\s*$/, '');
      paragraph.replaceWith(heading);
    }
  });

  // Colour list: after a "Colours..." heading, paragraphs that start with a bold colour code become code / name rows
  body.querySelectorAll('.dac-description__heading').forEach((heading) => {
    if (!/colou?rs?/i.test(heading.textContent)) {
      return;
    }

    let next = heading.nextElementSibling;

    while (next) {
      // Rows pasted from elsewhere can be separated by stray <br>, or wrapped in one extra <div>: skip / unwrap those
      if (next.tagName === 'BR') {
        const after = next.nextElementSibling;
        next.remove();
        next = after;
      } else if (next.tagName === 'DIV' && next.children.length && Array.from(next.children).every((child) => child.tagName === 'P')) {
        const rows = Array.from(next.children);
        next.replaceWith(...rows);
        next = rows[0];
      } else if (next.tagName === 'P') {
        buildColourRow(next);
        next = next.nextElementSibling;
      } else {
        break;
      }
    }
  });

  container.removeAttribute('data-tidy-pending');
};

if (!window.customElements.get('dac-product-tabs')) {
  class DacProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

      this.querySelectorAll('.dac-description').forEach(tidyDescription);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => this.onKeyDown(event, tab));
      });
    }

    onKeyDown(event, tab) {
      const index = this.tabs.indexOf(tab);
      let next = null;

      if (event.key === 'ArrowRight') {
        next = this.tabs[(index + 1) % this.tabs.length];
      } else if (event.key === 'ArrowLeft') {
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
      } else if (event.key === 'Home') {
        next = this.tabs[0];
      } else if (event.key === 'End') {
        next = this.tabs[this.tabs.length - 1];
      }

      if (next) {
        event.preventDefault();
        this.select(next, true);
      }
    }

    select(selectedTab, focus = false) {
      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));

        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');

        if (panel) {
          panel.hidden = !isSelected;
        }
      });

      if (focus) {
        selectedTab.focus();
      }
    }

    // Opens the tab / accordion item of that type and returns the element to scroll to (null if there is none)
    activate(type) {
      if (this.dataset.layout === 'accordion') {
        const item = this.querySelector(`.dac-product-tabs__accordion-item[data-tab-type="${type}"]`);
        const disclosure = item && item.querySelector('details');

        if (disclosure) {
          disclosure.open = true;
        }

        return item;
      }

      const tab = this.tabs.find((candidate) => candidate.dataset.tabType === type);

      if (!tab) {
        return null;
      }

      this.select(tab);

      return this;
    }
  }

  window.customElements.define('dac-product-tabs', DacProductTabs);
}
